import { useEffect, useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { REQUEST_ACTIONS } from '~/Constants/Actions.constants';
import { TIME_VALUES } from '~/Constants/Date.constants';
import { PROVIDER_SERVICE_URLS, EXTERNAL_API_URLS } from '~/Constants/Urls.constants';
import { requestReactQuery } from '~/Helpers/ReactQuery.helper';
import { routeReplacer } from '~/Helpers/String.helper';
import { useProvider, useHasAccess, AccessList, useUser } from '~/Hooks';
import { IErrorResponse } from '~/Models/DTO/Error.models';
import { useLocation } from 'react-router-dom';
import {
  IMerchant,
  IMerchantAPI,
  mapMerchantAPI2App,
  mapMerchantAPP2API,
  MerchantPayoutRate,
  IMerchantIBANValidationResponse,
} from '~/Models/DTO/Merchant.models';
import { ILocation } from '~/Models/DTO/Location.models';

function formatCentsToEuroString(amountInCents: number): string {
  // Convert cents to euros
  const amountInEuros = amountInCents / 100;

  // Format the number to a Euro string with two decimal places
  return amountInEuros.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });
}

function euroStringToCents(euroString: string): number {
  // Remove any non-numeric characters except for commas and dots
  const sanitizedString = euroString.replace(/[^0-9,.-]/g, '');

  // Replace comma with a dot to handle decimal separation
  const normalizedString = sanitizedString.replace(',', '.');

  // Parse the normalized string as a float
  const euroAmount = parseFloat(normalizedString);

  // Convert the Euro amount to cents
  const cents = Math.round(euroAmount * 100);

  return cents;
}

export const requestSetMerchantDefaultRate = (
  merchantUuid: string,
  merchantPayoutRateUuid: string,
) =>
  requestReactQuery({
    url: routeReplacer(PROVIDER_SERVICE_URLS.MERCHANT_SET_DEFAULT_RATE, {}),
    data: {
      merchantUuid: merchantUuid || '',
      merchantPayoutRateUuid: merchantPayoutRateUuid || '',
    },
    config: { method: 'POST' },
  });

export const useRequestGetMerchantPayoutRates = (merchantUuid = 'new') =>
  useQuery<any, IErrorResponse>(
    [REQUEST_ACTIONS.MERCHANT_GET_RATES, merchantUuid],
    async () => {
      if (!merchantUuid?.length || merchantUuid === 'new') {
        return null;
      }
      try {
        const rates = (await requestReactQuery({
          url: routeReplacer(PROVIDER_SERVICE_URLS.MERCHANT_GET_RATES, {
            merchantId: merchantUuid || '',
          }),
          config: { method: 'GET' },
        })) as { merchantPayoutRates: MerchantPayoutRate[]; merchantUuid: string };

        if (!rates?.merchantPayoutRates?.length) {
          return [];
        }

        let defaultRate = {} as MerchantPayoutRate;
        try {
          defaultRate = (await requestReactQuery({
            url: routeReplacer(PROVIDER_SERVICE_URLS.MERCHANT_GET_DEFAULT_RATE, {
              merchantId: merchantUuid || '',
            }),
            config: { method: 'GET' },
          })) as MerchantPayoutRate;
        } catch (er) {
          defaultRate = {} as MerchantPayoutRate;
        }
        const data = (rates?.merchantPayoutRates || []).map((rate: MerchantPayoutRate) => ({
          label: `${rate.actualRate.name} - ${formatCentsToEuroString(rate.actualRate.amountNet)}`,
          value: rate?.uuid,
          selected: rate?.uuid === defaultRate?.uuid ? true : undefined,
        }));
        return data;
      } catch (er) {
        throw er;
      }
    },
    {
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchOnMount: true,
      refetchInterval: TIME_VALUES.HOUR,
    },
  );

export const useRequestMerchants = () => {
  const { currentProvider } = useProvider();
  const venueId = useMemo(() => currentProvider?.organisationVenueId, [currentProvider?.uuid]);
  const response = useQuery<IMerchant[], IErrorResponse>(
    [REQUEST_ACTIONS.GET_MERCHANT_LIST, venueId],
    () =>
      !venueId
        ? ({
            ...{
              merchants: [],
            },
          } as any)
        : requestReactQuery({
            url: routeReplacer(PROVIDER_SERVICE_URLS.MERCHANT_LIST, {
              organisationId: venueId || '',
            }),
            config: { method: 'GET' },
          }),
    {
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchOnMount: true,
      refetchInterval: TIME_VALUES.HOUR,
    },
  );
  //The response format is nested, so we need to flatten it
  //@ts-ignore
  const data = (response?.data?.merchants?.map((merchant) => mapMerchantAPI2App(merchant)) ||
    []) as IMerchant[];
  const result = { ...response, data } as any;
  return result;
};

export const validateIBAN = async (iban: string) => {
  const url = EXTERNAL_API_URLS.VALIDATE_IBAN(iban);
  const response = await fetch(url, {
    method: 'GET',
  });
  return response.json();
};

export const validateVatId_EU_SOAP = async ({
  country,
  vatId,
}: {
  country: string;
  vatId: string;
}) => {
  const timeout = 1000;
  const controller = new AbortController();
  const id = setTimeout(() => controller.abort(), timeout);
  const requestOptions = {
    method: 'POST',
    timeout,
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ countryCode: country.toUpperCase(), vatNumber: vatId }),
    signal: controller.signal as any,
  };

  const result = { data: null, error: null } as any;
  try {
    const response = await fetch(EXTERNAL_API_URLS.VALIDATE_VATID_EU, requestOptions);
    result.data = (await response.json())?.data;
  } catch (e) {
    result.data = { valid: false, country, vatId };
    result.error = e;
  }
  clearTimeout(id);
  return result;
};

interface IUseCheckMerchantList {
  changeWarning: (value: boolean) => void;
  isOrganisationView?: boolean;
}
export const useValidateIBAN = () => {
  const validateIBAN = async (iban: string) => {
    try {
      const response = await requestReactQuery<IMerchantIBANValidationResponse>({
        url: routeReplacer(PROVIDER_SERVICE_URLS.MERCHANT_VALIDATE_IBAN, {
          iban,
        }),
        data: {},
        config: { method: 'POST' },
      });
      return response as IMerchantIBANValidationResponse;
    } catch (er: any) {
      return { isValid: false, error: er };
    }
  };

  return { validateIBAN };
};
export const useCheckMerchantList = ({ changeWarning }: IUseCheckMerchantList) => {
  const { hasAccess } = useHasAccess();
  const { currentProvider } = useProvider();
  const { pathname } = useLocation();
  const isOrganisationView = pathname?.includes('/organisation');
  const { DANGEROUSLY_isSuperAdmin: isSuperAdmin } = useUser();
  const [merchantList, setMerchantList] = useState<IMerchant[]>([]);
  const [warning, setWarning] = useState(false);

  const venueId = useMemo(() => currentProvider?.organisationVenueId, [currentProvider?.uuid]);

  const fetchLocationList = async (providerUUID: string) => {
    const response = (await requestReactQuery({
      url: routeReplacer(PROVIDER_SERVICE_URLS.LOCATION_LIST, {
        providerUUID,
      }),
      config: { method: 'GET' },
    }).catch((e) => ({ e, data: { merchants: [] } }))) as any;
    return response;
  };

  const fetchMerchantList = async () => {
    const response = (await requestReactQuery({
      url: routeReplacer(PROVIDER_SERVICE_URLS.MERCHANT_LIST, {
        organisationId: venueId || '',
      }),
      config: { method: 'GET' },
    }).catch((e) => ({ e, data: { merchants: [] } }))) as any;
    const data = (response?.merchants?.map((merchant: IMerchantAPI) =>
      mapMerchantAPI2App(merchant),
    ) || []) as IMerchant[];
    return data;
  };

  const fetchMerchantMissingInfo = async () => {
    const data: IMerchant[] = await fetchMerchantList();
    const locationList = isOrganisationView
      ? []
      : ((await fetchLocationList(currentProvider?.uuid || '')) as ILocation[]);

    const filterMerchantsWithLocation = data?.filter(({ locationIds }) => !!locationIds?.length);

    const filterRelevantMerchants = filterMerchantsWithLocation?.filter(({ locationIds }) =>
      locationIds?.find((id: string) =>
        locationList?.find(({ venueAddressId }) => venueAddressId?.toString() === id),
      ),
    );
    const hasMissingInfo = (isOrganisationView ? data : filterRelevantMerchants)?.find(
      ({ merchantDataComplete }) => !merchantDataComplete,
    );
    const warningValue =
      !!hasMissingInfo && hasAccess(AccessList.GLOBAL_WARNING) && !pathname.includes('/merchant');

    changeWarning(warningValue);
    setWarning(warningValue);
    if (data?.length === 0) {
      return;
    }
    setMerchantList(data);
  };

  useEffect(() => {
    if (pathname?.includes('/merchant')) {
      changeWarning(false);
    } else {
      changeWarning(warning);
    }
  }, [pathname]);

  useEffect(() => {
    if (!venueId) {
      changeWarning(false);
    }
  }, [venueId, currentProvider?.uuid]);

  useEffect(() => {
    if (venueId && hasAccess(AccessList.GLOBAL_WARNING)) {
      fetchMerchantMissingInfo();
    }
  }, [venueId, currentProvider?.uuid, hasAccess(AccessList.GLOBAL_WARNING)]);

  return {
    isOrganisationView,
    isSuperAdmin,
    merchantList,
  };
};

export const assignLocationsToMerchant = async ({
  uuid,
  location,
  locationuuid,
}: {
  uuid: string;
  location: number;
  locationuuid: string;
}) => {
  const response = await requestReactQuery({
    url: routeReplacer(PROVIDER_SERVICE_URLS.MERCHANT_ASSIGN_LOCATION, {}),
    data: {
      merchantUuid: uuid,
      locationId: location,
      clientId: 'Fitogram',
      locationUuid: locationuuid,
    },
    config: { method: 'POST' },
  });
  return response;
};

export const requestUpdateMerchantOrigin = (merchant: IMerchant) => ({
  merchant,
  data: { valid: true },
});

export const requestUpdateMerchant = async (merchant: IMerchant) => {
  const mapMerchant = mapMerchantAPP2API(merchant);
  return await requestReactQuery<IMerchantAPI>({
    url: routeReplacer(PROVIDER_SERVICE_URLS.MERCHANT_UPDATE, {}),
    data: mapMerchant,
    config: { method: 'PUT' },
  });
};

export const requestCreateMerchant = async (merchant: IMerchant) => {
  const defaultSinglePayoutRate =
    (typeof merchant?.defaultSinglePayoutRate === 'string' &&
      merchant?.defaultSinglePayoutRate.length &&
      euroStringToCents(merchant?.defaultSinglePayoutRate)) ||
    undefined;
  const mapMerchant = mapMerchantAPP2API({ ...merchant, defaultSinglePayoutRate });
  return await requestReactQuery<IMerchantAPI>({
    url: routeReplacer(PROVIDER_SERVICE_URLS.MERCHANT_UPDATE, {}),
    data: mapMerchant,
    config: { method: 'POST' },
  });
};
